import {useMemo, useReducer} from 'react';
import type {PlanType} from 'api/generated';

interface IUsePlanTableProps {
  plans: PlanType[];
  currentPlan: PlanType;
}

const usePlanTable = ({plans, currentPlan}: IUsePlanTableProps) => {
  const [showFeatureOverride, toggleFeatureOverride] = useReducer(s => !s, true);

  const plansWithMine = useMemo(() => {
    return plans.map(plan => (plan.variantOf === currentPlan.variantOf ? currentPlan : plan));
  }, [plans, currentPlan]);

  const features = useMemo(() => {
    const featureFields = plansWithMine[0].features;
    return featureFields.map((field, idx) => {
      return {
        name: field?.name,
        plans: Object.fromEntries(
          plansWithMine.map(plan => {
            const feature = typeof plan.features[idx]?.value === 'boolean' ? plan.features[idx].value : plan.features[idx]?.valueReadable;
            const featureOverride = (() => {
              if (!plan.featuresOverride) return null;
              return typeof plan.featuresOverride[idx]?.value === 'boolean'
                ? plan.featuresOverride[idx].value
                : plan.featuresOverride[idx]?.valueReadable;
            })();
            return [plan.name, {feature, featureOverride}];
          }),
        ),
      };
    });
  }, [plansWithMine]);

  const shouldHighlight = (plan: PlanType) => {
    return currentPlan.uid === plan.uid && (currentPlan.featuresOverride ? showFeatureOverride : true);
  };

  const getFeatureKey = (plan: PlanType) => {
    return plan.featuresOverride && showFeatureOverride ? 'featureOverride' : 'feature';
  };

  return {plansWithMine, features, showFeatureOverride, toggleFeatureOverride, shouldHighlight, getFeatureKey};
};

export default usePlanTable;
